/**
 * navigation.ts — Single source of truth for sidebar menus and permissions.
 *
 * Both DashboardLayout (sidebar rendering) and Onboarding (tutorial steps)
 * consume this config so permissions are never out of sync.
 */

import type { UserRole } from '../context/AuthContext';
import {
  LayoutDashboard, Mic, FileText, Users, Settings, FileEdit,
  BarChart3, ClipboardList, TrendingUp, Trophy, Award, Target,
  Book, Calendar, GitCompare, MessageSquare, Activity, Bell,
  Layers, Sparkles, Building2, Shield, Play, User, Headphones,
  TestTube2, Globe, Palette, Phone, UserCheck, Plug, Calculator, Archive,
} from 'lucide-react';

// ========================
// TYPES
// ========================

export interface NavItem {
  name: string;
  href: string;
  icon: any;
  roles: UserRole[];
  color?: string;
}

export interface NavCategory {
  label: string;
  items: NavItem[];
}

// ========================
// MENU CATEGORIES (non-operador)
// ========================

export const menuCategories: NavCategory[] = [
  {
    label: 'SUPER ADMIN',
    items: [
      { name: 'Visao Geral', href: '/superadmin/dashboard', icon: Globe, roles: ['superadmin'], color: 'var(--secondary)' },
      { name: 'Gestao de Empresas', href: '/superadmin/empresas', icon: Building2, roles: ['superadmin'], color: 'var(--primary)' },
      { name: 'White Label', href: '/superadmin/white-label', icon: Palette, roles: ['superadmin'], color: 'var(--chart-2)' },
      { name: 'Repositório', href: '/superadmin/repositorio', icon: Archive, roles: ['superadmin'], color: 'var(--chart-2)' },
    ],
  },
  {
    label: 'INTEGRACOES',
    items: [
      { name: 'Connectors', href: '/connectors', icon: Plug, roles: ['superadmin', 'admin'], color: 'var(--chart-3)' },
    ],
  },
  {
    label: 'PRINCIPAL',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-4)' },
      { name: 'Dashboard Customizado', href: '/dashboard-personalizado', icon: Sparkles, roles: ['admin', 'qualidade', 'supervisor'], color: 'var(--chart-1)' },
      { name: 'Notificacoes', href: '/notificacoes', icon: Bell, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-3)' },
      { name: 'Evolucao', href: '/historico-evolucao', icon: Activity, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-2)' },
    ],
  },
  {
    label: 'QUALIDADE',
    items: [
      { name: 'Dashboard Qualidade', href: '/qualidade', icon: Shield, roles: ['superadmin', 'admin', 'qualidade'], color: 'var(--primary)' },
      { name: 'Fila de Auditorias', href: '/qualidade/fila-auditorias', icon: Play, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--secondary)' },
      { name: 'Upload Auditoria', href: '/upload-auditoria', icon: FileEdit, roles: ['superadmin', 'admin', 'qualidade'], color: 'var(--chart-1)' },
      { name: 'Revisão Humana', href: '/revisao-humana', icon: UserCheck, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--destructive)' },
      { name: 'Lista de chamadas', href: '/chamadas', icon: Phone, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-4)' },
    ],
  },
  {
    label: 'MONITORAMENTO',
    items: [
      { name: 'Auditorias', href: '/auditorias', icon: FileText, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-1)' },
      { name: 'Questionarios', href: '/questionarios', icon: ClipboardList, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--accent)' },
      { name: 'Analytics', href: '/analitica', icon: TrendingUp, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-2)' },
      { name: 'Relatorios', href: '/relatorios', icon: BarChart3, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-3)' },
    ],
  },
  {
    label: 'TREINAMENTO',
    items: [
      { name: 'Dashboard Treinamento', href: '/treinamento-supervisor', icon: BarChart3, roles: ['supervisor'] as UserRole[], color: 'var(--chart-1)' },
      { name: 'Dashboard Treinamento', href: '/qualidade/treinamento', icon: BarChart3, roles: ['qualidade'] as UserRole[], color: 'var(--chart-1)' },
      { name: 'Dashboard Treinamento', href: '/admin/treinamento', icon: BarChart3, roles: ['admin', 'superadmin'] as UserRole[], color: 'var(--chart-1)' },
      { name: 'Treino', href: '/treino', icon: Mic, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--destructive)' },
      { name: 'Cenários', href: '/cenarios', icon: Headphones, roles: ['superadmin', 'admin', 'qualidade'], color: 'var(--secondary)' },
      { name: 'Calendario', href: '/calendario-treinos', icon: Calendar, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-2)' },
      { name: 'Scripts', href: '/biblioteca-scripts', icon: Book, roles: ['superadmin', 'admin', 'qualidade', 'supervisor', 'operador'], color: 'var(--accent)' },
      { name: 'Feedback 360', href: '/feedback-360', icon: MessageSquare, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--secondary)' },
    ],
  },
  {
    label: 'DESEMPENHO',
    items: [
      { name: 'Metas', href: '/metas', icon: Target, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-3)' },
      { name: 'Ranking', href: '/ranking', icon: Trophy, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-3)' },
      { name: 'Ligas', href: '/ligas', icon: Layers, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-4)' },
      { name: 'Certificados', href: '/certificados', icon: Award, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--chart-2)' },
      { name: 'Operadores', href: '/operadores', icon: Users, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--primary)' },
      { name: 'Comparacao', href: '/comparacao-operadores', icon: GitCompare, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--secondary)' },
    ],
  },
  {
    label: 'GESTAO',
    items: [
      { name: 'Dashboard Supervisor', href: '/dashboard-supervisor', icon: Users, roles: ['supervisor', 'admin'], color: 'var(--accent)' },
      { name: 'Dashboard Setores', href: '/dashboard-setores', icon: Building2, roles: ['admin', 'qualidade', 'supervisor'], color: 'var(--secondary)' },
      { name: 'Configuracoes', href: '/configuracoes', icon: Settings, roles: ['superadmin', 'admin', 'qualidade', 'supervisor'], color: 'var(--muted-foreground)' },
    ],
  },
  {
    label: 'ADMIN',
    items: [
      { name: 'Gestao de Usuarios', href: '/admin/usuarios', icon: Users, roles: ['superadmin', 'admin'], color: 'var(--destructive)' },
      { name: 'Calculadora', href: '/calculadora-enterprise', icon: Calculator, roles: ['superadmin'], color: 'var(--chart-1)' },
      { name: 'Testes E2E', href: '/superadmin/testes-e2e', icon: TestTube2, roles: ['superadmin'], color: 'var(--chart-2)' },
    ],
  },
];

// ========================
// OPERATOR CATEGORIES
// ========================

export const operatorCategories: NavCategory[] = [
  {
    label: 'MEU ESPACO',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['operador'], color: 'var(--chart-4)' },
      { name: 'Meu Perfil', href: '/meu-perfil', icon: User, roles: ['operador'], color: 'var(--primary)' },
      { name: 'Notificacoes', href: '/notificacoes', icon: Bell, roles: ['operador'], color: 'var(--chart-3)' },
      { name: 'Evolucao', href: '/historico-evolucao', icon: Activity, roles: ['operador'], color: 'var(--chart-2)' },
    ],
  },
  {
    label: 'TREINAMENTO',
    items: [
      { name: 'Centro de Treinamento', href: '/centro-treinamento', icon: Headphones, roles: ['operador'], color: 'var(--secondary)' },
      { name: 'Calendario', href: '/calendario-treinos', icon: Calendar, roles: ['operador'], color: 'var(--chart-2)' },
    ],
  },
  {
    label: 'MINHAS AVALIACOES',
    items: [
      { name: 'Auditorias', href: '/auditorias', icon: FileText, roles: ['operador'], color: 'var(--chart-1)' },
      { name: 'Feedback 360', href: '/feedback-360', icon: MessageSquare, roles: ['operador'], color: 'var(--secondary)' },
    ],
  },
  {
    label: 'DESEMPENHO',
    items: [
      { name: 'Metas', href: '/metas', icon: Target, roles: ['operador'], color: 'var(--chart-3)' },
      { name: 'Ranking', href: '/ranking', icon: Trophy, roles: ['operador'], color: 'var(--chart-3)' },
      { name: 'Ligas', href: '/ligas', icon: Layers, roles: ['operador'], color: 'var(--chart-4)' },
      { name: 'Certificados', href: '/certificados', icon: Award, roles: ['operador'], color: 'var(--chart-2)' },
    ],
  },
  {
    label: 'CONTA',
    items: [
      { name: 'Configuracoes', href: '/configuracoes', icon: Settings, roles: ['operador'], color: 'var(--muted-foreground)' },
    ],
  },
];

// ========================
// SUPERADMIN CATEGORY FILTER
// ========================

/** Categories visible to superadmin (all others are hidden) */
export const SUPERADMIN_VISIBLE_CATEGORIES = new Set(['SUPER ADMIN', 'INTEGRACOES', 'ADMIN', 'GESTAO']);

// ========================
// HELPERS
// ========================

/**
 * Returns the set of routes (hrefs) visible to a given role,
 * respecting the superadmin category filter.
 */
export function getVisibleRoutes(role: UserRole): Set<string> {
  const categories = role === 'operador' ? operatorCategories : menuCategories;

  const filtered = role === 'superadmin'
    ? categories.filter(cat => SUPERADMIN_VISIBLE_CATEGORIES.has(cat.label))
    : categories;

  const routes = new Set<string>();
  for (const cat of filtered) {
    for (const item of cat.items) {
      if (item.roles.includes(role)) {
        routes.add(item.href);
      }
    }
  }
  return routes;
}

/**
 * Returns the visible navigation categories for a given role,
 * with items already filtered. Empty categories are excluded.
 */
export function getVisibleCategories(role: UserRole): NavCategory[] {
  const categories = role === 'operador' ? operatorCategories : menuCategories;

  const filtered = role === 'superadmin'
    ? categories.filter(cat => SUPERADMIN_VISIBLE_CATEGORIES.has(cat.label))
    : categories;

  return filtered
    .map(cat => ({
      ...cat,
      items: cat.items.filter(item => item.roles.includes(role)),
    }))
    .filter(cat => cat.items.length > 0);
}

/**
 * Builds a map: route → category label, for the given role.
 * Used by Onboarding to assign tutorial steps to the correct category.
 */
export function getRouteCategoryMap(role: UserRole): Map<string, string> {
  const visibleCats = getVisibleCategories(role);
  const map = new Map<string, string>();
  for (const cat of visibleCats) {
    for (const item of cat.items) {
      map.set(item.href, cat.label);
    }
  }
  return map;
}
